import { inputCls, textareaCls } from "../../../components/admin/FormComponents"
import { splitBlanks, type TestQuestion } from "./model"

// Control de respuesta de los 10 tipos de pregunta, con la misma forma de valor que
// valida la API (ver isAnswerShapeValid): número, booleano, texto, arreglo de índices
// o arreglo de textos. `value === undefined` significa "sin responder".

type Props = {
  question: TestQuestion
  value: unknown
  onChange: (value: unknown) => void
}

const strings = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : [])

function Radio({ name, checked, onChange, label }: { name: string; checked: boolean; onChange: () => void; label: string }) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 py-1.5 text-sm text-gray-800 dark:text-gray-200">
      <input type="radio" name={name} checked={checked} onChange={onChange} className="mt-0.5" />
      <span>{label}</span>
    </label>
  )
}

export default function QuestionInput({ question, value, onChange }: Props) {
  const { question_type: type, content, index } = question
  const name = `q-${index}`

  switch (type) {
    case "multiple_choice":
      return (
        <div role="radiogroup" aria-label="Opciones">
          {strings(content.options).map((opt, i) => (
            <Radio key={i} name={name} checked={value === i} onChange={() => onChange(i)} label={opt} />
          ))}
        </div>
      )

    case "multiple_select": {
      const selected = new Set<number>(Array.isArray(value) ? (value as number[]) : [])
      return (
        <div>
          <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">Marca todas las correctas.</p>
          {strings(content.options).map((opt, i) => (
            <label key={i} className="flex cursor-pointer items-start gap-2.5 py-1.5 text-sm text-gray-800 dark:text-gray-200">
              <input
                type="checkbox"
                checked={selected.has(i)}
                className="mt-0.5"
                onChange={() => {
                  const next = new Set(selected)
                  if (next.has(i)) next.delete(i); else next.add(i)
                  onChange([...next].sort((a, b) => a - b))
                }}
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      )
    }

    case "true_false":
      return (
        <div role="radiogroup" aria-label="Verdadero o falso" className="flex gap-6">
          <Radio name={name} checked={value === true} onChange={() => onChange(true)} label="Verdadero" />
          <Radio name={name} checked={value === false} onChange={() => onChange(false)} label="Falso" />
        </div>
      )

    case "numerical":
      return (
        <input
          type="number"
          step="any"
          aria-label="Respuesta numérica"
          className={`${inputCls} max-w-xs`}
          value={typeof value === "number" && Number.isFinite(value) ? value : ""}
          onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
        />
      )

    case "short_answer":
      return (
        <input
          type="text"
          aria-label="Respuesta corta"
          className={inputCls}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
        />
      )

    case "essay":
      return (
        <textarea
          aria-label="Respuesta larga"
          rows={6}
          className={textareaCls}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
        />
      )

    case "code":
      return (
        <textarea
          aria-label="Código"
          rows={8}
          spellCheck={false}
          className={`${textareaCls} font-mono text-[13px]`}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
        />
      )

    case "fill_blank": {
      const segments = splitBlanks(content.textWithBlanks)
      const blanks = segments.length - 1
      const current = Array.isArray(value) ? (value as string[]) : Array.from({ length: blanks }, () => "")
      return (
        <p className="text-sm leading-9 text-gray-800 dark:text-gray-200">
          {segments.map((seg, i) => (
            <span key={i}>
              {seg}
              {i < blanks && (
                <input
                  type="text"
                  aria-label={`Espacio ${i + 1}`}
                  className={`${inputCls} mx-1 inline-block w-48 align-middle`}
                  value={current[i] ?? ""}
                  onChange={(e) => {
                    const next = Array.from({ length: blanks }, (_, k) => current[k] ?? "")
                    next[i] = e.target.value
                    onChange(next)
                  }}
                />
              )}
            </span>
          ))}
        </p>
      )
    }

    case "matching": {
      const left = strings(content.left)
      const right = strings(content.right)
      const current = Array.isArray(value) ? (value as Array<number | null>) : []
      return (
        <div className="space-y-2">
          {left.map((l, i) => (
            <div key={i} className="flex flex-wrap items-center gap-3 text-sm text-gray-800 dark:text-gray-200">
              <span className="min-w-[10rem] font-medium">{l}</span>
              <span aria-hidden>→</span>
              <select
                aria-label={`Pareja de ${l}`}
                className={`${inputCls} max-w-xs`}
                value={current[i] ?? ""}
                onChange={(e) => {
                  const next: Array<number | null> = left.map((_, k) => current[k] ?? null)
                  next[i] = e.target.value === "" ? null : Number(e.target.value)
                  // Con parejas sin elegir queda como incompleto y no se envía (ver isAnswered).
                  onChange(next)
                }}
              >
                <option value="">Elige…</option>
                {right.map((r, k) => <option key={k} value={k}>{r}</option>)}
              </select>
            </div>
          ))}
        </div>
      )
    }

    case "ordering": {
      const items = strings(content.items)
      const current = Array.isArray(value) ? (value as Array<number | null>) : []
      return (
        <div className="space-y-2">
          <p className="text-xs text-gray-500 dark:text-gray-400">Elige qué paso va en cada posición.</p>
          {items.map((_, pos) => (
            <div key={pos} className="flex items-center gap-3 text-sm text-gray-800 dark:text-gray-200">
              <span className="w-6 text-right font-medium">{pos + 1}.</span>
              <select
                aria-label={`Posición ${pos + 1}`}
                className={`${inputCls} max-w-md`}
                value={current[pos] ?? ""}
                onChange={(e) => {
                  const next: Array<number | null> = items.map((_, k) => current[k] ?? null)
                  next[pos] = e.target.value === "" ? null : Number(e.target.value)
                  onChange(next)
                }}
              >
                <option value="">Elige…</option>
                {items.map((it, k) => <option key={k} value={k}>{it}</option>)}
              </select>
            </div>
          ))}
        </div>
      )
    }

    default:
      return <p className="text-sm text-error-600">Tipo de pregunta no soportado: {String(type)}</p>
  }
}
